import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Building2, Loader2, CheckCircle2, LogOut } from "lucide-react";
import { base44, authClient, setActiveOrgId } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { useOrganisation } from "@/lib/OrganisationContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { isEmailVerificationError } from "@/lib/invitationErrors";

export default function Welcome() {
  const { user, logout } = useAuth();
  const { reloadOrganisations } = useOrganisation();

  const [invitations, setInvitations] = useState([]);
  const [invitationsError, setInvitationsError] = useState(null);
  const [invitationsLoading, setInvitationsLoading] = useState(true);
  const [acceptingId, setAcceptingId] = useState(null);
  const [acceptError, setAcceptError] = useState("");

  const [orgName, setOrgName] = useState("");
  const [message, setMessage] = useState("");
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    authClient.organization.listUserInvitations().then((r) => {
      if (cancelled) return;
      if (r.error) setInvitationsError(r.error);
      else setInvitations((r.data || []).filter((inv) => inv.status === "pending"));
    }).finally(() => {
      if (!cancelled) setInvitationsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  async function handleAccept(invitationId) {
    setAcceptingId(invitationId);
    setAcceptError("");
    try {
      const r = await authClient.organization.acceptInvitation({ invitationId });
      if (r.error) {
        setAcceptError(r.error.message || "Could not accept that invitation.");
        return;
      }
      const orgId = r.data?.invitation?.organizationId ?? r.data?.member?.organizationId ?? null;
      if (orgId) setActiveOrgId(orgId);
      await reloadOrganisations();
      window.location.href = "/";
    } catch (err) {
      setAcceptError(err.message || "Could not accept that invitation.");
    } finally {
      setAcceptingId(null);
    }
  }

  async function handleRequestAccess(e) {
    e.preventDefault();
    setRequestSubmitting(true);
    setRequestError("");
    try {
      await base44.functions.invoke("requestAccess", { organisation_name: orgName, message });
      setRequestSent(true);
    } catch (err) {
      if (err.status === 409) setRequestError("You already have an open access request. DI will be in touch.");
      else setRequestError(err.message || "Could not send your request.");
    } finally {
      setRequestSubmitting(false);
    }
  }

  // Real membership already redirected here from ProtectedRoute; this guards direct navigation too.
  if (Array.isArray(user?.organisations) && user.organisations.length > 0) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary mb-4">
            <Building2 className="w-7 h-7 text-primary-foreground" aria-hidden="true" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Welcome to InclusiveCore</h1>
          <p className="text-muted-foreground mt-2">
            {user?.email ? `Signed in as ${user.email}. ` : ""}
            You're not part of an organisation yet.
          </p>
        </div>

        <div className="bg-card rounded-2xl shadow-sm border border-border p-8 mb-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Your invitations</h2>
          {invitationsLoading && (
            <p role="status" className="text-sm text-muted-foreground">Checking for invitations…</p>
          )}
          {!invitationsLoading && invitationsError && isEmailVerificationError(invitationsError) && (
            <p className="text-sm text-muted-foreground">
              Verify your email address to see any pending invitations. Check your inbox for the verification link.
            </p>
          )}
          {!invitationsLoading && invitationsError && !isEmailVerificationError(invitationsError) && (
            <p className="text-sm text-muted-foreground">Couldn't check for invitations right now.</p>
          )}
          {!invitationsLoading && !invitationsError && invitations.length === 0 && (
            <p className="text-sm text-muted-foreground">No pending invitations for this email address.</p>
          )}
          {!invitationsLoading && !invitationsError && invitations.length > 0 && (
            <>
              {acceptError && (
                <div role="alert" className="mb-3 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{acceptError}</div>
              )}
              <ul className="space-y-3">
                {invitations.map((inv) => (
                  <li key={inv.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border">
                    <div>
                      <p className="text-sm font-medium text-foreground">{inv.organizationName}</p>
                      <p className="text-xs text-muted-foreground">as {inv.role}</p>
                    </div>
                    <Button size="sm" onClick={() => handleAccept(inv.id)} disabled={acceptingId === inv.id}>
                      {acceptingId === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accept"}
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="bg-card rounded-2xl shadow-sm border border-border p-8">
          <h2 className="text-lg font-semibold text-foreground mb-1">Request access</h2>
          <p className="text-sm text-muted-foreground mb-4">
            No invitation? Tell us who you are and Diverse Integration will get in touch.
          </p>
          {requestSent ? (
            <p role="status" className="flex items-center gap-2 text-sm text-foreground">
              <CheckCircle2 className="w-4 h-4 text-primary" aria-hidden="true" />
              Thanks — your request has been sent. We'll be in touch.
            </p>
          ) : (
            <form onSubmit={handleRequestAccess} className="space-y-4">
              {requestError && (
                <div role="alert" className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{requestError}</div>
              )}
              <div className="space-y-2">
                <Label htmlFor="orgName">Organisation name</Label>
                <Input id="orgName" value={orgName} onChange={(e) => setOrgName(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea id="message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} required />
              </div>
              <Button type="submit" className="w-full h-12 font-medium" disabled={requestSubmitting}>
                {requestSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : "Send request"}
              </Button>
            </form>
          )}
        </div>

        <p className="text-center text-sm mt-6">
          <button type="button" onClick={() => logout()} className="inline-flex items-center gap-1 text-primary font-medium hover:underline">
            <LogOut className="w-3.5 h-3.5" aria-hidden="true" />
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}
